import { useCallback, useEffect, useState } from "react";
import { api, ApiError, abrirDocumento, type FolhaFechada } from "../api";

/**
 * MINHAS FOLHAS — os meses que o DP fechou, para conferir e dar ciência.
 *
 * É a outra ponta do fechamento: o DP finaliza o mês e o documento aparece
 * aqui, no celular da pessoa, junto com o e-mail. Quem não tem e-mail no
 * cadastro — boa parte da equipe de campo — só tem este caminho, então ele
 * precisa funcionar sozinho.
 *
 * O PDF é o ARQUIVADO no fechamento, não um recálculo: é o mesmo papel que foi
 * enviado. Se o DP corrigir um dia depois, o documento que a pessoa viu continua
 * sendo o que ela viu.
 *
 * A CIÊNCIA registra que o colaborador conferiu — o substituto digital da
 * assinatura do rodapé. Não é concordância: o texto diz, com todas as letras,
 * que havendo divergência ele deve procurar o DP.
 */

const MESES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

function mesPorExtenso(competencia: string): string {
  const [ano, m] = competencia.split("-").map(Number);
  return `${MESES[m - 1] ?? competencia} de ${ano}`;
}

/** Minutos → "8h30"; negativo mantém o sinal. */
function horasLegiveis(minutos: number | null | undefined): string {
  if (minutos == null) return "—";
  const sinal = minutos < 0 ? "-" : "";
  const abs = Math.abs(Math.round(minutos));
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return m === 0 ? `${sinal}${h}h` : `${sinal}${h}h${String(m).padStart(2, "0")}`;
}

export default function MinhasFolhas() {
  const [folhas, setFolhas] = useState<FolhaFechada[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [abrindo, setAbrindo] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const r = await api.minhasFolhas();
      setFolhas(r.folhas);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível carregar as folhas.");
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /** Abre o PDF arquivado numa aba nova (com o token — ver abrirDocumento). */
  async function abrir(competencia: string) {
    setAbrindo(competencia);
    setErro(null);
    try {
      await abrirDocumento(
        `/api/minhas-folhas/${competencia}/pdf`,
        `folha-de-ponto-${competencia}.pdf`,
        "pdf",
      );
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível abrir o documento.");
    } finally {
      setAbrindo(null);
    }
  }

  async function confirmar(competencia: string) {
    setConfirmando(competencia);
    setErro(null);
    try {
      await api.darCiencia(competencia);
      await carregar();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível registrar a ciência.");
    } finally {
      setConfirmando(null);
    }
  }

  if (erro && !folhas) return <div className="aviso-erro">{erro}</div>;
  if (!folhas) return <div className="carregando">Carregando…</div>;

  if (folhas.length === 0) {
    return (
      <div className="vazio">
        Nenhuma folha fechada ainda. Quando o Departamento Pessoal finalizar o mês, o documento
        aparece aqui para você conferir.
      </div>
    );
  }

  return (
    <div className="folhas">
      {erro && <div className="aviso-erro">{erro}</div>}

      {folhas.map((f) => (
        <article key={f.competencia} className="folha">
          <header className="folha-topo">
            <span className="folha-mes">{mesPorExtenso(f.competencia)}</span>
            {f.ciencia_em ? (
              <span className="folha-selo folha-selo-ok">Conferido</span>
            ) : (
              <span className="folha-selo">A conferir</span>
            )}
          </header>

          {f.totais && (
            <div className="folha-numeros">
              <div>
                <span>Horas normais</span>
                <strong>{horasLegiveis(f.totais.horasNormais)}</strong>
              </div>
              <div>
                <span>Crédito</span>
                <strong>{horasLegiveis(f.totais.credito)}</strong>
              </div>
              <div>
                <span>Débito</span>
                <strong>{horasLegiveis(f.totais.debito)}</strong>
              </div>
              <div>
                <span>Saldo</span>
                <strong className={f.saldo_final < 0 ? "negativo" : undefined}>
                  {horasLegiveis(f.saldo_final)}
                </strong>
              </div>
            </div>
          )}

          <div className="folha-acoes">
            <button
              className="botao-principal botao-menor"
              onClick={() => abrir(f.competencia)}
              disabled={!f.tem_documento || abrindo === f.competencia}
            >
              {abrindo === f.competencia ? "Abrindo…" : "Ver a folha"}
            </button>

            {!f.ciencia_em && (
              <button
                className="botao-secundario"
                onClick={() => confirmar(f.competencia)}
                disabled={confirmando === f.competencia}
              >
                {confirmando === f.competencia ? "Registrando…" : "Confirmo que conferi"}
              </button>
            )}
          </div>

          <p className="folha-rodape">
            {f.ciencia_em
              ? `Você confirmou em ${f.ciencia_em}.`
              : "Confira as marcações antes de confirmar."}
            {" "}Havendo divergência, procure o Departamento Pessoal — a confirmação não substitui
            a correção.
          </p>
        </article>
      ))}
    </div>
  );
}
